import { useState } from 'react';

import { Box, Button, Stack, Typography, useColorScheme } from '@mui/joy';
import CalyxLogo from 'jsx:../assets/icons/logo.svg';
import CalyxLogoDark from 'jsx:../assets/icons/logoDark.svg';

import { ContactUsForm } from '../pages/developerPortal/components/forms/ContactUsForm';
import { useGlobalStore } from '../stores/useGlobalStore';
import { UPGRADE_SOURCE } from '../utils/constants/upgradeSources';

const BANNER_HEIGHT = 56;

const formatExpiry = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.max(0, Math.ceil((date.getTime() - Date.now()) / 86400000));
  const on = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return days > 0 ? `Expires in ${days} day${days === 1 ? '' : 's'} · ${on}` : 'Expired';
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const demoSession = useGlobalStore((s) => s.demoSession);
  const { mode } = useColorScheme();
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <>
      {demoSession && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: `${BANNER_HEIGHT}px`,
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: { xs: 2, md: 3 },
            bgcolor: (theme) => theme.palette.background.surface,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: 'xs',
          }}
        >
          <Stack direction='row' alignItems='center' gap={1.5} sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              {mode === 'dark' ? (
                <CalyxLogoDark width={120} height={26} />
              ) : (
                <CalyxLogo width={120} height={26} />
              )}
            </Box>
            <Typography level='body-sm' noWrap sx={{ color: 'text.primary' }}>
              You’re exploring a shared{' '}
              <Typography component='span' color='primary' fontWeight='lg'>
                demo
              </Typography>{' '}
              of <strong>{demoSession.feature_label}</strong>
            </Typography>
          </Stack>

          <Stack direction='row' alignItems='center' gap={1.5} sx={{ flexShrink: 0 }}>
            <Typography
              level='body-xs'
              noWrap
              sx={{ color: 'text.tertiary', display: { xs: 'none', sm: 'block' } }}
            >
              {formatExpiry(demoSession.expires_at)}
            </Typography>
            <Button
              size='sm'
              variant='secondary'
              color='primary'
              onClick={() => setContactOpen(true)}
            >
              Contact us
            </Button>
            <Button
              size='sm'
              variant='solid'
              color='neutral'
              onClick={() => window.open('/', '_blank', 'noopener,noreferrer')}
            >
              Log in
            </Button>
          </Stack>
        </Box>
      )}
      <Box
        id='layout'
        sx={{
          position: 'fixed',
          top: demoSession ? `${BANNER_HEIGHT}px` : 0,
          mx: 'auto',
          width: 1,
          height: `calc(100vh - ${demoSession ? BANNER_HEIGHT : 0}px)`,
          overflowY: 'scroll',
        }}
      >
        {children}
      </Box>

      <ContactUsForm
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        source={UPGRADE_SOURCE.DEMO_BANNER_CONTACT_US}
        defaultEmail={demoSession?.email}
      />
    </>
  );
};

export { Layout as DemoLayout };
